import { CommentProps } from "@/models/Comment"
import { StructuredDescription } from "@/models/EditorData"
import { Epic } from "@/models/Epic"
import { FileMetaData } from "@/models/File"
import { FeContext, Item, ItemStatus } from "@/models/Item"
import { Sprint } from "@/models/Sprint"
import { Task } from "@/models/Task"
import { User } from "@/models/User"

import { RetroNote } from "./RetroNote"
import { Team, TeamType } from "./Team"

import type { CalendarEventDto } from "@/models/Calendar"
import type { GoalStatus } from "@/models/Goal"
import type { Idea, IdeaPreviewSseConversion } from "@/models/Idea"
import type { ToDo } from "@/models/ToDo"

// export type ResourceType = "ACCOUNT" | "PROJECT" | "SPRINT" | "ITEM" | "TODO_ITEM" | "COMMENT" | "USER" | "TEAM" | "INVITATION" | "STORY_POKER" | "EPIC" | "FILE_METADATA" | "JIRA_BACKLOG_IMPORT_COMPLETED";
export const resourceTypes = [
  "ACCOUNT",
  "PROJECT",
  "SPRINT",
  "ITEM",
  "TODO_ITEM",
  "TODO_ITEM_CHANGE",
  "COMMENT",
  "USER",
  "TEAM",
  "INVITATION",
  "STORY_POKER_ESTIMATION_UPDATE",
  "EPIC",
  "IDEA",
  "FILE_METADATA",
  "RETROSPECTIVE_NOTE_CHANGED",
  "SPRINT_REVIEW",
  "ITEM_SNAPSHOT",
  "SUBSCRIPTION",
  "RETROSPECTIVE",
  "STORY_POKER",
  "JIRA_BACKLOG_IMPORT_COMPLETED",
  "ITEM_CREATED",
  "GOAL",
  "ACCOUNT_ENABLED",
  "SUBSCRIPTION",
  "COMMENT_CHANGE",
  "EPIC_CHANGED",
  "AI_RESPONSE_READY",
  "AI_RESPONSE_READY_FOR_RETROSPECTIVE_SUMMARY",
  "AI_RESPONSE_READY_FOR_ACCEPTANCE_CRITERIA",
  "AI_CONVERSATION"
] as const

export const resourceEvents = [
  "NOTIFICATION_UPDATE",
  "ITEM_DELETED",
  "ITEM_CREATED",
  "ITEM_PRIORITY_CHANGED",
  "ITEM_UPDATED",
  "COMMENT_CHANGE",
  "FIVE_WHYS",
  "FILE_METADATA_CHANGED",
  "EPIC_CHANGED",
  "IDEA_ACTIVITY",
  "GOAL_CHANGED",
  "SPRINT_CHANGED",
  "CALENDAR_EVENT_CHANGED",
  "DEMO_PROJECT_CREATED",
  "MILESTONE_CHANGED",
  "TODO_ITEM_CHANGED",
  "TODO_ITEM_METRICS_UPDATED",
  "ATTACHMENT_DOWNLOAD_FINISHED"
] as const

export const commentSourceTypes = [
  "SPRINT_REVIEW",
  "ITEM_STATUS_CHANGE",
  "IDEA_STATUS_CHANGE",
  "AI_FEEDBACK",
  "AUDIT",
  "ITEM",
  "DEMO_VIDEO"
] as const

export type ResourceType = (typeof resourceTypes)[number]
export type ResourceEvent = (typeof resourceEvents)[number]
export type CommentSourceType = (typeof commentSourceTypes)[number]

export type UpdateObjectValue<T> = <K extends keyof T>(params: { id: keyof T; value: T[K] }) => void

export type CallbackFunction = () => void

export type BlockTab = {
  id: string
  label: string
  isLast?: boolean
  hideButton?: boolean
  countOptions?: { number: string; notify: string }
  disabled?: boolean
  disabledAction?: boolean
}

export type IdAndName = {
  id: string
  name: string
  accountId?: string
}

export interface NameIdAndUrl {
  id: string
  name: string
  urlName: string
  accountId?: string
  prefix?: string
  hasScrumSprint?: boolean
  type?: TeamType
}

export type ProgressBarConfig = {
  currentValue: number
  maxValue: number
  label?: string
}

export interface ServerSendEventMessage {
  type: ResourceType
}

export interface ServerSendAiResponseReady {
  sprintId: string
  summary: string
  responseStatus?: string
  type: ResourceType
}

export interface ServerSendAiResponseReadyForRetrospectiveSummary {
  retrospectiveId: string
  summary: string
  responseStatus?: string
  type: ResourceType
}

export interface ServerSendAiResponseReadyForAcceptanceCriteria {
  itemId: string
  acceptanceCriteria: StructuredDescription | null
  responseStatus?: string
  type: ResourceType
}

export interface ServerSendRetroUpdate {
  retrospectiveId: string
  type: ResourceType
  newNotes: RetroNote[]
}

export interface ServerSendStoryPokerUpdate {
  storyPokerId: string
  type: ResourceType
  userIds: string[]
}

export interface ServerSendEventNotificationPriority {
  priorityType?: string
  priority?: number
  oldPriority?: number
  priorityHolderId?: string
  oldPriorityHolderId?: string
}

export interface AttachmentDto {
  id: string
  name: string
  size: number
  deleted: boolean
}

export type ResourceChangeType = "CREATED" | "UPDATED" | "DELETED"

export interface ServerSendChange<T> {
  oldValue: T
  newValue: T
}

export interface ServerSendAttachmentUpdate {
  attachmentsChanged: AttachmentDto
  changeType: ResourceChangeType
  type: ResourceEvent
}

export interface PostMortemDto {
  itemId: string
  resolution: string
  whys: string[]
  status: ItemStatus
}

export interface ServerSendPostMortemUpdate {
  fiveWhys: PostMortemDto
  type: ResourceEvent
}

export interface ServerSendEventNotification {
  newNumberOfUnreadNotifications?: number
  hasImportant?: boolean
  notificationType?: string
  type: ResourceEvent
  fileMetadataId?: string
  item?: Item
  changedPriorities?: ServerSendEventNotificationPriority[]
  statusChangeCommentChanged?: CommentProps | null
  teamChanged?: {
    oldValue: Team
    newValue: Team
  }
  itemTypeChanged?: {
    oldValue: string
    newValue: string
  }
  titleChanged?: {
    oldValue: string
    newValue: string
  }
  epicChanged?: {
    oldValue: Epic
    newValue: Epic
  }
  attachmentsChanged?: {
    oldValue: FileMetaData[]
    newValue: FileMetaData[]
  }
  attachmentsChangedV2?: {
    oldValue: FileMetaData[]
    newValue: FileMetaData[]
  }
  taskChanged?: {
    oldValue: Task[]
    newValue: Task[]
  }
  statusChanged?: {
    oldValue: ItemStatus
    newValue: ItemStatus
  }
  ownerChanged?: {
    oldValue: User
    newValue: User
  }
  feContext?: FeContext
}

export interface DemoProjectCreationFinished {
  recreationFinished: boolean
  type: ResourceEvent
}

export interface TodoItemChangeDto {
  countOfUncompletedTodoItems: number
  accountId: string
  type: ResourceType
}

export interface TodoItemChangedNotificationDto {
  accountId: string
  todoItemId: string
  todoItem: ToDo | null
  changedFields: Partial<ToDo>
  changeType: ResourceChangeType
  type: "TODO_ITEM_CHANGED"
}

export interface TodoItemMetricsUpdatedNotificationDto {
  accountId: string
  type: ResourceEvent
}

export interface ServerSendEpicUpdateNotification {
  epic: Epic
  changeType: ResourceChangeType
  type: ResourceEvent
  itemStatusesChanged?: {
    newValue: Record<string, { milestoneStatus: string }>
  }
  milestoneChanged?: {
    oldValue?: { id?: string } | null
    newValue?: { id?: string } | null
  }
  changedPriorities?: ServerSendEventNotificationPriority[]
  structuredDescriptionChanged?: {
    oldValue: StructuredDescription
    newValue: StructuredDescription
  }
  labelChanged?: {
    oldValue: string
    newValue: string
  }
}

/** The backend sends no activity for a deleted Idea, only CREATED and UPDATED. */
export interface ServerSendIdeaActivityNotification {
  idea: Partial<Idea> & IdeaPreviewSseConversion & { id: string; projectId: string }
  changeType: Exclude<ResourceChangeType, "DELETED">
  type: ResourceEvent
  event?: { initiator?: User }
  attachmentsMetadata?: { attachmentsCount: number }
  feContext?: FeContext
}

/** A Goal was created or updated (content, level or priority) by anyone in its project. */
export interface ServerSendGoalChangedNotification {
  goal: {
    id: string
    status: GoalStatus
    content: string
    important: string
    timeline: string
    videoUrl?: string | null
  }
  changeType: ResourceChangeType
  type: ResourceEvent
  statusChanged?: ServerSendChange<GoalStatus | null> | null
}

export interface ServerSendSprintUpdateNotification {
  sprint: Sprint
  changeType: ResourceChangeType
  type: ResourceEvent
  utilizationRatioChanged?: ServerSendChange<number>
  wipChanged?: ServerSendChange<number>
}

export interface ServerSendMilestoneUpdateNotification {
  projectId: string
  changeType: ResourceChangeType
  type: "MILESTONE_CHANGED"
}

export interface ServerSendCalendarEventChangedNotification {
  event: CalendarEventDto
  changeType: ResourceChangeType
  type: ResourceEvent
  startDateChanged?: {
    oldValue: string
    newValue: string
  }
  endDateChanged?: {
    oldValue: string
    newValue: string
  }
  descriptionChanged?: {
    oldValue: string
    newValue: string
  }
  allDayChanged?: {
    oldValue: boolean
    newValue: boolean
  }
  userIdsChanged?: {
    oldValue: string[]
    newValue: string[]
  }
  feContext?: FeContext
}

export type BacklogContext = {
  backlogName: string
  filter?: any
  sprintId?: string | undefined
} | null

export type FoundBacklogItem = {
  context: BacklogContext
  position: number
  filter?: any
}
